import React from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import { FaceplateImg } from '../faceplates/FaceplateImg';
import { DeliveryDetails } from './DeliveryDetails';
import { itemConfirmationMessages } from './confirmationMessages';
import { useFormatMessage } from '../../intl/intlHooks';
import { lookupPromotionalCostByLineItem } from '../../mapi/ApplyPromosProvider';
import ItemCostConfirmation from './ItemCostConfirmation';

export const ItemBase = ({
    amount,
    quantity,
    currency,
    isSelfBuy,
    recipientEmail,
    confirmationEmail,
    deliveryMethod,
    faceplate,
    shippingGroup,
    name,
    recipientFirstName,
    lineItemDiscount,
    perCardDiscount,
    productPromoDisplayName,
    productPurchaseFeeAmount,
    lineItemTotalCost,
}) => {

    return (
        <div className="item-confirmation">
            <div className="item-confirmation--top">
                <div className="item-confirmation--top--left">
                    <div className="item-confirmation-faceplate">
                        <FaceplateImg faceplate={faceplate} cssClass="item-confirmation-image" />
                    </div>
                    <div className="item-confirmation-name">
                        <div>{`${recipientFirstName} ${name}`}</div>
                    </div>
                    {productPromoDisplayName && (
                        <div className="item-confirmation-promotion-description">
                            <div>{`${productPromoDisplayName}`}</div>
                        </div>
                    )}
                </div>
                <div className="item-confirmation--top--right">
                    <ItemCostConfirmation
                        quantity={quantity}
                        amount={amount}
                        lineItemDiscount={lineItemDiscount}
                        perCardDiscount={perCardDiscount}
                        currency={currency}
                        shippingGroup={shippingGroup}
                        productPurchaseFeeAmount={productPurchaseFeeAmount}
                    />
                </div>
            </div>
            <div className="item-confirmation--bottom">
                <div className="item-confirmation--bottom--left">
                    <DeliveryDetails
                        recipientEmail={recipientEmail}
                        isSelfBuy={isSelfBuy}
                        deliveryMethod={deliveryMethod}
                        confirmationEmail={confirmationEmail}
                        shipTo={deliveryMethod === 'PHYSICAL' ? shippingGroup.toAddress : {}}
                        shippingMethod={
                            deliveryMethod === 'PHYSICAL' ? shippingGroup.shippingMethod.shippingMethodDisplayName : ''
                        }
                    />
                </div>
                <div className="item-confirmation--bottom--right">
                    <div className="item-confirmation-total">
                        <FormattedNumber style="currency" currency={currency || 'USD'} value={lineItemTotalCost} />
                    </div>
                </div>
            </div>
        </div>
    );
};

ItemBase.defaultProps = {
    currency: 'USD',
    isSelfBuy: false,
    recipientEmail: '',
    confirmationEmail: '',
    shippingGroup: {},
    lineItemDiscount: 0,
    perCardDiscount: 0,
    faceplate: {},
    name: '',
    product: {
        fees: [],
    },
    productPromoDisplayName: null,
    productPurchaseFeeAmount: null,
};

ItemBase.propTypes = {
    currency: PropTypes.string,
    isSelfBuy: PropTypes.bool,
    recipientEmail: PropTypes.string,
    confirmationEmail: PropTypes.string,
    amount: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    deliveryMethod: PropTypes.string.isRequired,
    faceplate: PropTypes.object,
    shippingGroup: PropTypes.object,
    name: PropTypes.string,
    recipientFirstName: PropTypes.string.isRequired,
    lineItemDiscount: PropTypes.number,
    perCardDiscount: PropTypes.number,
    product: PropTypes.shape({
        fees: PropTypes.arrayOf(
            PropTypes.object,
        )
    }),
    productPromoDisplayName: PropTypes.any,
    productPurchaseFeeAmount: PropTypes.any,
    lineItemTotalCost: PropTypes.number.isRequired,
};
/**
 * The item component wrapped for optimized rendering.
 *
 * SEE: https://reactjs.org/docs/react-api.html#reactmemo
 */
export const PureItem = React.memo(ItemBase);
/**
 * Extract the specific props to be used in the optimized pure item component.
 *
 * Given the commonly shaped line item blob this component unpacks the
 * props and passes them to the optimized PureItem component.
 */
export const ItemConfirmation = ({ item, confirmationEmail, promotions }) => {
    const formatMessage = useFormatMessage();
    const recipientName = item.shippingGroup ? item.shippingGroup.toAddress.fullName : item.recipientName;
    const recipientFirstName = item.isSelfBuy
        ? formatMessage(itemConfirmationMessages.selfBuyRecipientLabel)
        : `${recipientName.split(' ')[0]}'s`;
    const { id, amount, quantity } = item;
    
    const defaultLineItemCost = quantity * amount; 
    const { lineItemPromoCost = defaultLineItemCost, discountRewardAmount = 0.00, perCardDiscountRewardAmount = 0.00 } = lookupPromotionalCostByLineItem(promotions, id) || {};

    const fees = item.product ? item.product.fees : [];
    const productPurchaseFee = fees.filter((fee) => (
        Number(fee.amount) !== 0 && fee.name === "plasticActivationFeeAmount"
    ));
    let productPromoDisplayName = null;
    
    if ('appliedPromos' in promotions && 'product' in item && 'promo' in item.product &&
        promotions.appliedPromos.includes(item.product.promo.promoCode)
    ) {
        productPromoDisplayName = item.product.promo.promoDisplayShortDesc
    }
    const productPurchaseFeeAmount = (productPurchaseFee.length > 0 && 'amount' in productPurchaseFee[0])
        ? Number(productPurchaseFee[0].amount)
        : null;
    const lineItemTotalCost = lineItemPromoCost + ((productPurchaseFeeAmount ?? 0) * item.quantity);
    return (
        <PureItem
            {...item}
            recipientFirstName={recipientFirstName}
            confirmationEmail={confirmationEmail}
            lineItemDiscount={discountRewardAmount}
            perCardDiscount={perCardDiscountRewardAmount}
            productPromoDisplayName={productPromoDisplayName}
            productPurchaseFeeAmount={productPurchaseFeeAmount}
            lineItemTotalCost={lineItemTotalCost}
        />
    );
};

ItemConfirmation.propTypes = {
    item: PropTypes.object.isRequired,
    confirmationEmail: PropTypes.string.isRequired,
    promotions: PropTypes.object
};

ItemConfirmation.defaultProps = {
    promotions: {}
};

export const ItemConfirmationList = ({ items, confirmationEmail, promotions }) => (
    <ul className="item-confirmation-list">
        {items.map(item => (
            <li key={item.id}>
                <ItemConfirmation item={item} confirmationEmail={confirmationEmail} promotions={promotions} />
            </li>
        ))}
    </ul>
);

ItemConfirmationList.propTypes = {
    items: PropTypes.array.isRequired,
    confirmationEmail: PropTypes.string.isRequired,
    promotions: PropTypes.object
};

ItemConfirmationList.defaultProps = {
    promotions: {}
};
